import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import './CateringPage.css';

/**
 * Changelog ID: TCC-UI-007
 * Author: Cascade AI
 * Date: 2025-05-28
 * Summary: Created dedicated CateringPage component to replace generic LandingPage implementation
 * Following WindSurf Global Rules for structured, testable code
 */

const CateringPage: React.FC = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elementsToAnimate = document.querySelectorAll('.scroll-animate');
    elementsToAnimate.forEach((el) => observer.observe(el));

    return () => {
      elementsToAnimate.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, []);
  /**
   * Changelog ID: TCC-UI-008
   * Author: Cascade AI
   * Date: 2025-07-07
   * Summary: Updated gallery items to use existing product images
   * Following WindSurf Global Rules for structured, testable code
   */
  const galleryItems = [
    {
      image: '/images/products/triple-chocolate-chunk-sourdough.jpg',
      title: 'Corporate Breakfast Platters',
      description: 'Perfect morning treats for meetings and events'
    },
    {
      image: '/images/products/classic-chocolate-chip.jpg',
      title: 'Wedding Dessert Tables',
      description: 'Beautiful dessert spreads for your special day'
    },
    {
      image: '/images/catering-lt-1.jpg',
      title: 'Event Cookie Boxes',
      description: 'Customizable cookie collections for any occasion'
    },
    {
      image: '/images/products/unicorn-cookies.jpg',
      title: 'Birthday Party Packages',
      description: 'Celebration desserts that delight guests of all ages'
    }
  ];

  const features = [
    {
      title: 'Custom Baking',
      description: 'Each order is freshly baked to your specifications with premium ingredients'
    },
    {
      title: 'Customized Designs',
      description: 'Brand your desserts with logos, colors, or themes that match your event'
    },
    {
      title: 'Dietary Accommodations',
      description: 'Options for gluten-free, nut-free, and vegan dietary requirements'
    }
  ];

  return (
    <div className="catering-page">
      <PageHeader
        title="Catering & Business Orders"
        description="We offer custom orders for businesses, events, and catering. Contact us to discuss your needs!"
      />
      {/* No additional hero banner needed - standardized page-header provides consistent styling */}
      
      {/* CTA Banner with button - separate from hero for better placement */}
      <section className="cta-banner container scroll-animate fade-in-up-init">
        <div className="cta-content">
          <h2>Ready to elevate your next event?</h2>
          <p>Our custom catering options are perfect for corporate events, weddings, and special occasions.</p>
          <Link to="/contact" className="btn btn-primary" aria-label="Contact Us Today for catering inquiries">
            Contact Us Today
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ marginLeft: '8px' }} role="img" aria-hidden="true" focusable="false">
              <title>Arrow right</title>
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="container features-section scroll-animate fade-in-up-init" aria-labelledby="features-heading">
        <div className="section-header">
          <span className="section-subtitle">Why Choose Us</span>
          <h2 id="features-heading">Exceptional Catering Solutions</h2>
          <p className="section-description">
            Discover why The Caffeinated Crumb is the preferred choice for events of all sizes.
          </p>
        </div>
        
        <div className="features-grid" role="list">
          {features.map((feature, index) => (
            <div key={index} className="feature-item" role="listitem" tabIndex={0}>
              <div className="feature-icon" aria-hidden="true">
                {getFeatureIcon(index)}
              </div>
              <div className="feature-content">
                <h3 id={`feature-heading-${index}`}>{feature.title}</h3>
                <p aria-labelledby={`feature-heading-${index}`}>{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      <section className="container gallery-section scroll-animate fade-in-up-init" aria-labelledby="catering-options-heading">
        <div className="section-header">
          <span className="section-subtitle">Our Services</span>
          <h2 id="catering-options-heading">Catering Options</h2>
          <p className="section-description">
            From corporate events to weddings, we provide custom dessert solutions for any occasion.
          </p>
        </div>
        
        <div className="gallery-grid" role="list">
          {galleryItems.map((item, index) => (
            <div key={index} className="gallery-item" role="listitem" tabIndex={0}>
              <div className="image-container">
                <img 
                  src={item.image} 
                  alt={`${item.title}: ${item.description}`} 
                  loading="lazy" 
                />
              </div>
              <div className="gallery-item-info">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Main Content Section */}
      <section className="container landing-content">
        <div className="section-header">
          <h2>Custom Catering For Every Occasion</h2>
        </div>
        <div className="landing-description">
          <p>
            The Caffeinated Crumb specializes in creating memorable dessert experiences for corporate
            events, weddings, and special occasions. Our team works closely with you to design the
            perfect dessert menu that matches your event's theme and dietary requirements.
          </p>
          <p>
            From elegant wedding cookie tables to branded corporate gift boxes, we take pride in
            delivering exceptional quality and presentation. Every item is freshly baked using
            premium ingredients, ensuring your guests enjoy an unforgettable dessert experience.
          </p>
          <p>
            Our flexible catering options include delivery, setup, and custom packaging. We can
            accommodate various dietary restrictions including gluten-free, nut-free, and vegan
            options to ensure all your guests can enjoy our delicious treats.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <div className="container">
        <section className="cta-section">
          <div className="cta-content">
            <h2>Ready to Create a Custom Order?</h2>
            <p>
              Contact us today to discuss your catering needs and let us create the perfect
              dessert experience for your next event.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Contact Us
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2">
                <title>Arrow right</title>
                <path d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z" />
                <path d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z" />
              </svg>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

/**
 * Helper function to get feature icons based on index
 * This provides visual elements for each feature
 */
const getFeatureIcon = (index: number) => {
  const iconTitles = ['Custom Baking', 'Flexible Delivery', 'Customized Designs', 'Dietary Accommodations'];
  const title = iconTitles[index % iconTitles.length];
  
  const icons = [
    // Cake/pastry icon
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="feature-svg" role="img" aria-hidden="true" focusable="false">
      <title>{title}</title>
      <path d="M15 1.784l-4.616 2.689a1 1 0 0 1-1-1.732l5-2.887a.6.6 0 0 1 .6 0l5 2.887a1 1 0 1 1-1 1.732L15 1.784ZM15 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6ZM1.783 12l-1.75 2a1 1 0 0 0 .5 1.566l20.7 8.208c.486.193 1.033-.102 1.205-.605L23 20.478c.172-.503-.13-.979-.633-.999L1.783 12ZM1 16.25v2.243c0 .803.861 1.312 1.576.932l16.948-8.999A.75.75 0 0 0 19.95 9.5H3.135A1.15 1.15 0 0 0 2 10.657V14.5c0 .414.336.75.75.75H8.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75v-.75Z" />
    </svg>,
    // Delivery icon
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="feature-svg" role="img" aria-hidden="true" focusable="false">
      <title>{title}</title>
      <path d="M3.375 4.5C2.339 4.5 1.5 5.34 1.5 6.375V13.5h12V6.375c0-1.036-.84-1.875-1.875-1.875h-8.25ZM13.5 15h-12v2.625c0 1.035.84 1.875 1.875 1.875h.375a3 3 0 1 1 6 0h3a.75.75 0 0 0 .75-.75V15Z" />
      <path d="M8.25 19.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0ZM15.75 6.75a.75.75 0 0 0-.75.75v11.25c0 .087.015.17.042.248a3 3 0 0 1 5.958.464c.853-.175 1.522-.935 1.464-1.883a18.659 18.659 0 0 0-3.732-10.104 1.837 1.837 0 0 0-1.47-.725H15.75Z" />
      <path d="M19.5 19.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0Z" />
    </svg>,
    // Custom design icon
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="feature-svg" role="img" aria-hidden="true" focusable="false">
      <title>{title}</title>
      <path fillRule="evenodd" d="M20.599 1.5c-.376 0-.743.111-1.055.32l-5.08 3.385a18.747 18.747 0 0 0-3.471 2.987 10.04 10.04 0 0 0-2.258 3.86c-.298.775-.908 1.369-1.683 1.653a10.172 10.172 0 0 0-2.57 1.118c-.41.257-.782.567-1.112.924-1.513 1.643-1.413 4.205.226 5.73 1.403 1.303 3.522 1.384 5.031.217a8.858 8.858 0 0 0 .728-.562c.81-.608 1.452-1.358 1.914-2.2.24-.435.324-.065.484-.398.215-.448.523-.825.913-1.127a2.825 2.825 0 0 1 3.053-.263 18.75 18.75 0 0 0 3.986 1.386c1.15.277 2.198-.31 2.674-1.418a2.25 2.25 0 0 0-.302-2.209c-.506-.577-1.134-1.039-1.836-1.358a10.187 10.187 0 0 1-2.49-1.576 5.392 5.392 0 0 1-1.11-1.352 2.02 2.02 0 0 1-.328-1.036 1 1 0 0 0-.287-.787 1.039 1.039 0 0 0-.794-.227 2.235 2.235 0 0 1-1.886-.767 1 1 0 0 0-.585-.258c-.02-.001-.498-.057-.518-.057Z" clipRule="evenodd" />
      <path d="M14.5 11.5c.173 0 .345-.028.508-.082l3.5-1.166a1 1 0 0 0 .65-1.252 3.5 3.5 0 0 0-6.651 1.012 1 1 0 0 0 .977 1.488c.165 0 .332-.026.495-.08l3.5-1.166a1 1 0 0 1 1.046.282 1 1 0 0 1 .12 1.042 3.5 3.5 0 0 1-6.651 1.012 1 1 0 0 1-.26-.758 1 1 0 0 1 .5-.783 1 1 0 0 1 .395-.133l.125-.017a1 1 0 0 0 .161-.032l.159-.053a1 1 0 0 0 .186-.081l.292-.141a1 1 0 0 0 .23-.145 1 1 0 0 0 .212-.218 1 1 0 0 0 .129-.255c.006-.019.011-.039.017-.058l.027-.108a1 1 0 0 0 .007-.067l.003-.084a1 1 0 0 0-.007-.118l-.029-.15a1 1 0 0 0-.092-.262 1 1 0 0 0-.122-.178 1 1 0 0 0-.252-.208l-.057-.034-3.5-1.166a1 1 0 0 1-.282-1.747 3.5 3.5 0 0 1 6.651 1.012 1 1 0 0 1-.977 1.488Z" />
    </svg>,
    // Dietary accommodations icon
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="feature-svg" role="img" aria-hidden="true" focusable="false">
      <title>{title}</title>
      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
    </svg>
  ];
  
  return icons[index % icons.length];
};

export default CateringPage;
